'use client'

import { useState, useMemo } from 'react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilteredData } from '@/hooks/useFilters'
import { useFilterStore } from '@/stores/filterStore'
import { KPI_DEFS, SCORE_TIERS, scoreTier, computeEngineerKPIs } from '@/lib/calculations'
import Badge from '@/components/ui/Badge'

function TierBadge({ tier, value, unit }: { tier: number; value: number | null; unit: string }) {
  if (tier < 0 || value === null) return <span className="inline-block px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-400 dark:bg-slate-800">N/A</span>
  const t = SCORE_TIERS[tier]
  const display = Number.isInteger(value) ? value : value.toFixed(1)
  return <span className={`inline-block px-2 py-0.5 text-xs font-semibold ${t.color}`} title={t.label}>{display}{unit}</span>
}

export default function ScorecardPage() {
  const { isLoading, tasks, timesheets, activeEngineers, engineerRoles, odooUrl } = useDashboardData()
  const { filterParams } = useFilteredData()
  const { engineer } = useFilterStore()
  const [view, setView] = useState<'table' | 'compare'>('table')

  const engineers = useMemo(() =>
    engineer !== 'all' ? [engineer] : [...activeEngineers].sort(),
    [engineer, activeEngineers]
  )

  const results = useMemo(() => {
    const r: Record<string, ReturnType<typeof computeEngineerKPIs>> = {}
    engineers.forEach(eng => { r[eng] = computeEngineerKPIs(eng, tasks, timesheets, filterParams) })
    return r
  }, [engineers, tasks, timesheets, filterParams])

  // Previous period for comparison
  const prevRange = useMemo(() => {
    const now = new Date()
    const curFrom = filterParams.customDateFrom ? new Date(filterParams.customDateFrom) : new Date(now.getTime() - filterParams.period * 864e5)
    const curTo = filterParams.customDateTo ? new Date(filterParams.customDateTo) : now
    const days = Math.round((curTo.getTime() - curFrom.getTime()) / 864e5)
    const prevTo = new Date(curFrom.getTime() - 864e5)
    const prevFrom = new Date(prevTo.getTime() - days * 864e5)
    return { from: prevFrom.toISOString().slice(0, 10), to: prevTo.toISOString().slice(0, 10) }
  }, [filterParams])

  const prevResults = useMemo(() => {
    if (view !== 'compare') return {}
    const r: Record<string, ReturnType<typeof computeEngineerKPIs>> = {}
    engineers.forEach(eng => { r[eng] = computeEngineerKPIs(eng, tasks, timesheets, filterParams, prevRange.from, prevRange.to) })
    return r
  }, [view, engineers, tasks, timesheets, filterParams, prevRange])

  if (isLoading) return <div className="text-slate-400 text-center py-16">Loading...</div>

  return (
    <div className="space-y-5">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold">Engineer Scorecard <span className="text-xs font-normal text-slate-400 ml-1">{engineers.length} engineers</span></h3>
            <div className="flex gap-1.5 mt-1.5">
              {SCORE_TIERS.map(t => (
                <span key={t.key} className={`inline-block px-2 py-0.5 text-[0.65rem] font-semibold ${t.color}`}>{t.label}</span>
              ))}
            </div>
          </div>
          <div className="flex gap-1">
            {(['table', 'compare'] as const).map(v => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-1 text-sm ${view === v ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
                {v === 'table' ? 'Scorecard' : 'Period Comparison'}
              </button>
            ))}
          </div>
        </div>

        {view === 'table' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400 font-[family-name:var(--font-display)]">Engineer</th>
                  <th className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400 font-[family-name:var(--font-display)]">Role</th>
                  {KPI_DEFS.map(d => (
                    <th key={d.k} className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400 font-[family-name:var(--font-display)]" title={d.label}>{d.label}</th>
                  ))}
                  <th className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400 font-[family-name:var(--font-display)]">Overall</th>
                  <th className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400 font-[family-name:var(--font-display)]">Details</th>
                </tr>
              </thead>
              <tbody>
                {engineers.map(eng => {
                  const role = engineerRoles[eng] || 'L1'
                  const kpis = results[eng]
                  let tierSum = 0, tierCount = 0
                  return (
                    <tr key={eng} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                      <td className="px-2 py-2 font-medium">{eng}</td>
                      <td className="px-2 py-2"><Badge>{role}</Badge></td>
                      {KPI_DEFS.map(d => {
                        const val = kpis[d.k as keyof typeof kpis] as number | null
                        const tier = scoreTier(val, d)
                        if (tier >= 0) { tierSum += tier; tierCount++ }
                        return <td key={d.k} className="px-2 py-2"><TierBadge tier={tier} value={val} unit={d.unit} /></td>
                      })}
                      {(() => {
                        const avgTier = tierCount > 0 ? Math.round(tierSum / tierCount) : -1
                        return (
                          <td className="px-2 py-2">
                            {avgTier >= 0 ? <span className={`inline-block px-2 py-0.5 text-xs font-semibold ${SCORE_TIERS[avgTier].color}`}>{SCORE_TIERS[avgTier].label}</span> : <span className="text-slate-400 text-xs">N/A</span>}
                          </td>
                        )
                      })()}
                      <td className="px-2 py-2 text-xs text-slate-400">
                        {kpis._done} done, {kpis._active} active, {kpis._totalH.toFixed(0)}h
                        {kpis._estTaskCount !== undefined && <span className="block text-slate-400">{kpis._estTaskCount} est, {kpis._dlTaskCount} dl</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {view === 'compare' && (
          <div>
            <p className="text-[0.65rem] text-slate-400 mb-3">
              Current period vs previous period ({prevRange.from} to {prevRange.to}).
              <span className="text-emerald-700 dark:text-emerald-400 ml-2">▲ improved</span>
              <span className="text-red-700 dark:text-red-400 ml-2">▼ declined</span>
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400">Engineer</th>
                    <th className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400">Role</th>
                    {KPI_DEFS.map(d => (
                      <th key={d.k} className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400">{d.label}</th>
                    ))}
                    <th className="text-left px-2 py-2 text-[0.65rem] uppercase tracking-wider text-slate-400">Overall</th>
                  </tr>
                </thead>
                <tbody>
                  {engineers.map(eng => {
                    const role = engineerRoles[eng] || 'L1'
                    const cur = results[eng]
                    const prev = prevResults[eng]
                    let curTierSum = 0, curTierCount = 0

                    return (
                      <tr key={eng} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <td className="px-2 py-2 font-medium">{eng}</td>
                        <td className="px-2 py-2"><Badge>{role}</Badge></td>
                        {KPI_DEFS.map(d => {
                          const curVal = cur[d.k as keyof typeof cur] as number | null
                          const prevVal = prev ? prev[d.k as keyof typeof prev] as number | null : null
                          const curTier = scoreTier(curVal, d)
                          if (curTier >= 0) { curTierSum += curTier; curTierCount++ }

                          let arrow = ''
                          if (curVal !== null && prevVal !== null) {
                            const delta = Math.abs(curVal - prevVal)
                            if (delta > 0.5) {
                              const better = d.higherBetter ? curVal > prevVal : curVal < prevVal
                              arrow = better ? ' ▲' : ' ▼'
                            }
                          }

                          return (
                            <td key={d.k} className="px-2 py-2">
                              <TierBadge tier={curTier} value={curVal} unit={d.unit} />
                              {arrow && <span className={`text-[0.65rem] ml-0.5 ${arrow === ' ▲' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`} title={prevVal !== null ? `was ${prevVal.toFixed(1)}${d.unit}` : ''}>{arrow}</span>}
                            </td>
                          )
                        })}
                        {(() => {
                          const avgTier = curTierCount > 0 ? Math.round(curTierSum / curTierCount) : -1
                          return (
                            <td className="px-2 py-2">
                              {avgTier >= 0 ? <span className={`inline-block px-2 py-0.5 text-xs font-semibold ${SCORE_TIERS[avgTier].color}`}>{SCORE_TIERS[avgTier].label}</span> : <span className="text-slate-400 text-xs">N/A</span>}
                            </td>
                          )
                        })()}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Task Detail Table — shows underlying tasks for each engineer */}
      {engineers.map(eng => {
        const kpis = results[eng]
        if (!kpis._doneTasks?.length && !kpis._activeTasks?.length) return null
        const allTasks = [...kpis._doneTasks, ...kpis._activeTasks].sort((a, b) => b.last_stage.localeCompare(a.last_stage))

        return (
          <div key={eng} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
            <h3 className="text-base font-semibold mb-1">
              {eng} — Task Details
              <span className="text-sm font-normal text-slate-400 ml-2">
                {kpis._done} done, {kpis._active} active
                {kpis._estTaskCount > 0 && ` · ${kpis._estTaskCount} with estimates`}
                {kpis._dlTaskCount > 0 && ` · ${kpis._dlTaskCount} with deadlines`}
              </span>
            </h3>
            <p className="text-xs text-slate-400 mb-4">All tasks used in scorecard calculations for this period.</p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    {['Task', 'Project', 'Stage', 'Lead (d)', 'Cycle (d)', 'Estimated', 'Actual', 'Est. Acc.', 'Deadline', 'On Time', 'Assigned'].map(h => (
                      <th key={h} className="text-left px-2 py-2 text-xs uppercase tracking-wider text-slate-400 font-[family-name:var(--font-display)]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allTasks.slice(0, 50).map(t => {
                    const estAcc = t.alloc_h > 0 && t.eff_h > 0
                      ? Math.max(0, 100 - Math.abs(100 - (t.eff_h / t.alloc_h) * 100))
                      : null
                    const onTime = t.deadline && t.last_stage ? t.last_stage <= t.deadline : null

                    return (
                      <tr key={t.id} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                        <td className="px-2 py-2 max-w-[220px] truncate" title={t.name}>
                          <a href={`${odooUrl}/odoo/project.project/${t.projId}/tasks/${t.id}`} target="_blank" rel="noopener noreferrer"
                            className="text-slate-900 dark:text-slate-100 hover:underline font-medium">
                            {t.name}
                          </a>
                        </td>
                        <td className="px-2 py-2 text-slate-400 text-xs truncate max-w-[140px]">{t.proj}</td>
                        <td className="px-2 py-2"><Badge>{t.stage}</Badge></td>
                        <td className="px-2 py-2 text-slate-500">{t.lead ?? '—'}</td>
                        <td className="px-2 py-2 text-slate-500">{t.cycle ?? '—'}</td>
                        <td className="px-2 py-2 text-slate-500">{t.alloc_h ? `${t.alloc_h}h` : '—'}</td>
                        <td className="px-2 py-2 text-slate-500">{t.eff_h ? `${t.eff_h}h` : '—'}</td>
                        <td className="px-2 py-2">
                          {estAcc !== null
                            ? <Badge variant={estAcc >= 80 ? 'success' : estAcc >= 50 ? 'warning' : 'danger'}>{estAcc.toFixed(0)}%</Badge>
                            : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-2 py-2 text-slate-500 text-xs">{t.deadline || '—'}</td>
                        <td className="px-2 py-2">
                          {onTime !== null
                            ? <Badge variant={onTime ? 'success' : 'danger'}>{onTime ? 'Yes' : 'No'}</Badge>
                            : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-2 py-2 text-slate-400 text-xs">{t.assigned || '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {allTasks.length > 50 && (
                <p className="text-xs text-slate-400 mt-2">Showing first 50 of {allTasks.length} tasks.</p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
